<template>
    <hover-card
        class="app-hover-card"
        :for-multiselect="forMultiselect"
        :title="appName"
        :metrics="metrics"
        :is-loading-metrics="isLoadingMetrics"
        :has-metrics-error="hasMetricsError"
        :metrics-title="$t('Metrics')"
        :metrics-subtitle="$t('Last 30 days')"
        :height="height">
        <template #actions>
            <div class="app-hover-card__actions">
                <pendo-tag
                    class="app-hover-card__tag"
                    :label="integrationStatusLabel"
                    :type="integrationStatusType"
                    size="small"
                    subtle />
            </div>
        </template>
        <template #subtitle>
            <div class="app-hover-card__platform">
                <pendo-app-icon
                    :app="app"
                    :size="12" />
                <span>
                    {{ platformLabel }}
                </span>
            </div>
        </template>
    </hover-card>
</template>

<script>
import { PendoTag, PendoTooltip, PendoAppIcon } from '@pendo/components';
import { mapGetters } from 'vuex';
import HoverCard from '@/common/hover-card/hover-card.vue';
import { AppsService } from '@/common/apps/apps.service';
import { isWebBasedApp } from '@/utils/apps';
import AppMetricsAggregation from './aggs/app-metrics.aggregation';
import { v4 as uuid } from 'uuid';
import {
    createMetricsObservable,
    handleMetricsResponse,
    sendHoverCardTrackEvent,
} from '@/common/hover-card/hover-card-metrics.utils';
import { LOADING_STATES } from '@/constants';

export default {
    name: 'AppHoverCard',
    directives: {
        PendoTooltip,
    },
    components: {
        HoverCard,
        PendoAppIcon,
        PendoTag,
    },
    props: {
        appId: {
            type: String,
            required: true,
        },
        forMultiselect: {
            type: Boolean,
            default: false,
        },
        height: {
            type: String,
            default: undefined,
        },
    },
    data () {
        return {
            visitorsValue: 0,
            accountsValue: 0,
            isLoadingMetrics: true,
            hasMetricsError: false,
        };
    },
    computed: {
        ...mapGetters({
            getAppById: 'apps/getAppById',
            hasAccountLevelAnalyticsEnabled: 'subscription/hasAccountLevelAnalyticsEnabled',
        }),
        app () {
            return this.getAppById(this.appId) || {};
        },
        appName () {
            return this.app.displayName || this.app.name || this.$t('appHoverCard.Unknown App');
        },
        platform () {
            return this.app.platform || 'web';
        },
        platformLabel () {
            return AppsService.getPlatformLabel(this.app) || this.$t('appHoverCard.Unknown');
        },
        isIntegrated () {
            return this.app.integrated || false;
        },
        integrationStatusLabel () {
            if (this.isIntegrated) {
                return isWebBasedApp(this.platform)
                    ? this.$t('appHoverCard.Installed')
                    : this.$t('appHoverCard.Integrated');
            }

            return isWebBasedApp(this.platform)
                ? this.$t('appHoverCard.Not installed')
                : this.$t('appHoverCard.Not integrated');
        },
        integrationStatusType () {
            return this.isIntegrated ? 'success' : 'info';
        },
        visitorsMetric () {
            return {
                label: this.$t(
                    'countableHoverCard.visitorsMetric.Unique {visitorCount, plural, one {visitor} other {visitors}}',
                    { visitorCount: this.visitorsValue },
                ),
                icon: 'user',
                value: this.visitorsValue,
            };
        },
        accountsMetric () {
            if (!this.hasAccountLevelAnalyticsEnabled) {
                return null;
            }

            return {
                label: this.$t(
                    'countableHoverCard.accountsMetric.Unique {accountCount, plural, one {account} other {accounts}}',
                    { accountCount: this.accountsValue },
                ),
                icon: 'users',
                value: this.accountsValue,
            };
        },
        metrics () {
            return [this.visitorsMetric, this.accountsMetric].filter(Boolean);
        },
    },
    created () {
        this.STREAM_ID = uuid();

        const metrics$ = createMetricsObservable({
            aggregation: AppMetricsAggregation,
            streamId: this.STREAM_ID,
            componentParams: { id: 'appId' },
            component: this,
            store: this.$store,
            onAggStart: () => {
                this.aggStartTime = Date.now();
            },
        });

        this.metricsSubscription = metrics$.subscribe((response) => {
            const updates = handleMetricsResponse(response);
            Object.assign(this, updates);

            if (response.status === LOADING_STATES.RESOLVED) {
                sendHoverCardTrackEvent({
                    eventType: 'app',
                    aggStartTime: this.aggStartTime,
                    pageLocation: this.$route?.name,
                });
            }
        });
    },
    unmounted () {
        this.metricsSubscription?.unsubscribe();
    },
};
</script>

<style scoped lang="less">
.app-hover-card {
    &__actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        align-self: stretch;
        height: 23px;

        &-buttons-wrapper {
            display: flex;
            align-items: center;
            gap: 10px;
        }
    }

    &__tag {
        max-width: 197px;

        :deep(> span) {
            text-transform: none;
            overflow: hidden;
            text-overflow: ellipsis;
            font-weight: 400;
        }
    }

    &__view-settings-button {
        height: 16px;
        color: var(--palette-gray-70);

        &:hover {
            color: var(--palette-gray-100);
        }
    }

    &__platform {
        display: flex;
        align-items: center;
        color: var(--palette-gray-70);
        font-size: 12.64px;
        font-weight: 400;
        padding-top: 8px;
        gap: 8px;

        span {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
}
</style>
